import { useEffect } from 'react';
import type { MapLabel } from '@common/schemas/entities/location.schema';
import { BUFFER_SIZE } from './types';

interface LabelCanvasInput {
    canvasRef: React.RefObject<HTMLCanvasElement | null>;
    mapLabels: MapLabel[];
    selectedLabelUid: string | null;
    hoveredLabelUid: string | null;
    dimensions: { width: number; height: number };
    zoomRef: React.MutableRefObject<number>;
    panRef: React.MutableRefObject<{ x: number; y: number }>;
    isCustomUpload: boolean;
    customMapImageRef?: React.RefObject<HTMLImageElement | null>;
    cameraTick: number;
}

/**
 * Side-effect hook: draws map text labels onto the dedicated label canvas.
 */
export function useLabelCanvas({
    canvasRef,
    mapLabels,
    selectedLabelUid,
    hoveredLabelUid,
    dimensions,
    zoomRef,
    panRef,
    isCustomUpload,
    customMapImageRef,
    cameraTick,
}: LabelCanvasInput): void {
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const { width, height } = dimensions;
        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        ctx.clearRect(0, 0, width, height);

        const isCustom = isCustomUpload && !!customMapImageRef?.current;
        const mW = isCustom ? customMapImageRef!.current!.naturalWidth : BUFFER_SIZE;
        const mH = isCustom ? customMapImageRef!.current!.naturalHeight : BUFFER_SIZE;

        let rW = Math.min(width, height);
        let rH = Math.min(width, height);
        if (isCustom) {
            const ratio = mW / mH;
            if (width / height > ratio) { rH = height; rW = height * ratio; }
            else { rW = width; rH = width / ratio; }
        }

        const zoom = zoomRef.current;
        const pan = panRef.current;

        const toScreen = (x: number, y: number) => ({
            sx: (x / mW) * rW * zoom + pan.x,
            sy: (y / mH) * rH * zoom + pan.y,
        });

        for (const label of mapLabels) {
            const { sx, sy } = toScreen(label.x, label.y);
            const isSelected = selectedLabelUid === label.uid;
            const isHovered = hoveredLabelUid === label.uid;

            ctx.save();
            ctx.translate(sx, sy);
            if (label.rotation) {
                ctx.rotate(label.rotation);
            }

            // Adjust font size based on zoom and canvas scaling
            const scaledFontSize = label.fontSize * (rW / mW) * zoom;
            const fontString = `${label.fontStyle === 'italic' ? 'italic' : 'normal'} ${label.fontWeight === 'bold' ? 'bold' : 'normal'} ${scaledFontSize}px ${label.fontFamily}`;
            
            ctx.font = fontString;
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.lineJoin = 'round';
            ctx.miterLimit = 2;
            
            if (label.outlineWidth > 0) {
                ctx.strokeStyle = label.outlineColor;
                ctx.lineWidth = label.outlineWidth * (rW / mW) * zoom;
                ctx.strokeText(label.text, 0, 0);
            }

            ctx.fillStyle = label.color;
            ctx.fillText(label.text, 0, 0);

            // Draw selection/hover bounding box
            if (isSelected || isHovered) {
                const metrics = ctx.measureText(label.text);
                const textWidth = metrics.width;
                const textHeight = scaledFontSize; // rough approximation
                
                ctx.strokeStyle = isSelected ? '#3b82f6' : 'rgba(255, 255, 255, 0.5)';
                ctx.lineWidth = 1;
                ctx.setLineDash(isSelected ? [] : [4, 4]);
                ctx.strokeRect(-textWidth / 2 - 4, -textHeight / 2 - 4, textWidth + 8, textHeight + 8);
                
                if (isSelected) {
                    ctx.fillStyle = '#ffffff';
                    const s = 6; // handle size
                    ctx.fillRect(-textWidth / 2 - 4 - s/2, -textHeight / 2 - 4 - s/2, s, s);
                    ctx.fillRect(textWidth / 2 + 4 - s/2, -textHeight / 2 - 4 - s/2, s, s);
                    ctx.fillRect(-textWidth / 2 - 4 - s/2, textHeight / 2 + 4 - s/2, s, s);
                    ctx.fillRect(textWidth / 2 + 4 - s/2, textHeight / 2 + 4 - s/2, s, s);
                }
            }

            ctx.restore();
        }
    }, [
        canvasRef, mapLabels, selectedLabelUid, hoveredLabelUid,
        dimensions, zoomRef, panRef, isCustomUpload, customMapImageRef, cameraTick
    ]);
}
